import { FAQEntity, FeatureName, HowToStep, schemaGenerator } from "@utils/schemaGenerator";
import { bool } from "aws-sdk/clients/redshiftdata";
import { Analytics } from "@vercel/analytics/next";
import cx from "classnames";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { FC, PropsWithChildren, ReactElement, useState } from "react";
import MobileNavBar from "../MobileNavBar";
import TopNavBlock from "../TopNavBlock";
import UnknownNavBar from "../UnknownNavBar";
import Footer from "./Footer";
import { container_classname } from "./constants";
import { createContext, useContext } from "react";
import GlobalFormModal from "../GlobalFormModal";

// Create context for layout values
const LayoutContext = createContext<{
    showBanner: boolean;
}>({
    showBanner: true,
});

// Hook to use layout context
export const useLayout = (): { showBanner: boolean } => useContext(LayoutContext);

export const openWhatsapp = (text: string): void => {
    const phone = 917825877730;
    const message = encodeURIComponent(text);
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
};
const webAppUrl = process.env.NEXT_PUBLIC_GB_APP_PUBLIC_URL;

interface TopNavBarContent {
    title: string;
    btnText: string;
    link: string;
}

interface NavMenuProps {
    cx?: string;
    showBanner?: boolean;
    isFreezone?: boolean;
    isArabic?: boolean;
    topNavBarContent?: TopNavBarContent;
}

export const handleLogin = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();
    window.open(`${webAppUrl ?? "https://app.gallabox.com"}/login`);
};

export const handleSignup = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    e.preventDefault();
    window.open(`${webAppUrl ?? "https://app.gallabox.com"}/signup`);
};
const HIDE_BANNER_ROUTE = "/free-zone";

// eslint-disable-next-line react/display-name
const NavMenu = React.forwardRef<HTMLDivElement, NavMenuProps>((props, ref) => {
    const { showBanner, isFreezone, isArabic, topNavBarContent } = props;
    const router = useRouter();
    const [open, setOpen] = useState(false);
    const [showGlobalFormModal, setShowGlobalFormModal] = useState(false);
    const shouldHideBanner = router.pathname === HIDE_BANNER_ROUTE;
    const showHelloBar = showBanner && !shouldHideBanner;

    const handleBannerButtonClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        e.preventDefault();
        setShowGlobalFormModal(true);
    };

    const handleGlobalFormClose = () => {
        setShowGlobalFormModal(false);
    };

    const handleGlobalFormSuccess = () => {
        setShowGlobalFormModal(false);
    };

    return (
        <div className={`${showHelloBar ? "lg:pb-10 pb-28" : "lg:pb-0 pb-14"}`}>
            <div ref={ref} className={cx("w-full bg-white left-0 z-10", props.cx)} style={{ boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)" }}>
                {showHelloBar && (
                    <div className={`lg:block hidden ${showHelloBar ? "h-15" : ""}`}>
                        <TopNavBlock
                            titleContent={topNavBarContent?.title ?? "Tired of your faulty WhatsApp API provider? Migrate to Gallabox and get 3 months FREE on the annual plan!"}
                            btnText={topNavBarContent?.btnText ?? "I'm interested"}
                            link={topNavBarContent?.link ?? "https://gallabox.com/lp/dont-broadcast"}
                            customStyles={"sticky top-0"}
                            textStyles={"text-lg"}
                            onButtonClick={topNavBarContent?.link ? undefined : handleBannerButtonClick}
                        />
                    </div>
                )}
                <div className={`${container_classname} p-4 flex flex-col md:items-center md:justify-between md:flex-row gap-10`}>
                    <div className="flex flex-row items-center justify-between">
                        <a href="/" className={"focus:outline-none focus:shadow-outline"} aria-label="Gallabox">
                            <img alt="Gallabox" src="/gallabox-logo.svg" className="mx-auto lg:mx-0 w-32" width="auto" height="auto" />
                        </a>
                        <button
                            className="md:hidden focus:outline-none focus:shadow-outline"
                            onClick={() => {
                                setOpen((prev) => !prev);
                            }}
                        >
                            <svg fill="currentColor" viewBox="0 0 20 20" className="w-5 h-5" style={{ width: "22.79px", height: "20.06px" }}>
                                {!open && (
                                    <path
                                        d="M23.2106 0.168457H0.875855C0.750507 0.168457 0.647949 0.271014 0.647949 0.396362V2.2196C0.647949 2.34495 0.750507 2.44751 0.875855 2.44751H23.2106C23.3359 2.44751 23.4385 2.34495 23.4385 2.2196V0.396362C23.4385 0.271014 23.3359 0.168457 23.2106 0.168457ZM23.2106 17.9451H0.875855C0.750507 17.9451 0.647949 18.0476 0.647949 18.173V19.9962C0.647949 20.1216 0.750507 20.2241 0.875855 20.2241H23.2106C23.3359 20.2241 23.4385 20.1216 23.4385 19.9962V18.173C23.4385 18.0476 23.3359 17.9451 23.2106 17.9451ZM23.2106 9.05676H0.875855C0.750507 9.05676 0.647949 9.15932 0.647949 9.28467V11.1079C0.647949 11.2333 0.750507 11.3358 0.875855 11.3358H23.2106C23.3359 11.3358 23.4385 11.2333 23.4385 11.1079V9.28467C23.4385 9.15932 23.3359 9.05676 23.2106 9.05676Z"
                                        fill="#2D3748"
                                    />
                                )}
                                {open && (
                                    <path
                                        d="M20.5812 0.945068L0.24646 17.9451C0.121112 17.9451 0.0185547 18.0476 0.0185547 18.173V19.9962C0.0185547 20.1216 0.121112 20.2241 0.24646 20.2241L20.5812 3.22412C20.7065 3.22412 20.8091 3.12156 20.8091 2.99622V1.17297C20.8091 1.04763 20.7065 0.945068 20.5812 0.945068ZM20.5812 18.7217L0.24646 1.72168C0.121112 1.72168 0.0185547 1.82424 0.0185547 1.94959V3.77283C0.0185547 3.89818 0.121112 4.00074 0.24646 4.00074L20.5812 21.0007C20.7065 21.0007 20.8091 20.8982 20.8091 20.7728V18.9496C20.8091 18.8242 20.7065 18.7217 20.5812 18.7217Z"
                                        fill="#2D3748"
                                    />
                                )}
                            </svg>
                        </button>
                    </div>

                    {open ? <MobileNavBar handleLogin={handleLogin} /> : null}
                    <UnknownNavBar open={open} handleLogin={handleLogin} showBanner={showHelloBar} isFreezone={isFreezone} isArabic={isArabic} />
                </div>
                {showHelloBar && (
                    <div className="lg:hidden block">
                        <TopNavBlock
                            titleContent={<span className="px-2">Tired of your faulty WhatsApp API provider? Migrate to Gallabox and get 3 months FREE!</span>}
                            btnText="I'm interested"
                            customStyles={"sticky top-0"}
                            isMobile={true}
                            link="https://gallabox.com/lp/dont-broadcast"
                            onButtonClick={handleBannerButtonClick}
                        />
                    </div>
                )}
                {showGlobalFormModal && <GlobalFormModal onClose={handleGlobalFormClose} onSuccess={handleGlobalFormSuccess} />}
                <style jsx>
                    {`
                        .login-btn {
                            font-weight: 500;
                            font-size: 16px;
                            color: #4a5568;
                            cursor: pointer;
                        }
                        .login-btn:hover {
                            text-decoration: none;
                        }
                    `}
                </style>
            </div>
        </div>
    );
});

interface SocialCTAProps {
    name: string;
    brandColor: string;
    href: string;
    icon: ReactElement;
    gradient?: boolean;
    gradientBorder?: string;
}

export const SocialCTA: FC<SocialCTAProps> = (props) => {
    const { name, href, brandColor, icon, gradient = false, gradientBorder } = props;
    return (
        <a href={href} aria-label={name ?? "social"} target="_blank" rel="noreferrer" className="p-2 text-lg lg:text-base rounded-full transition ease-linear duration-100">
            {icon}
            <style jsx>{`
                color: #000;
                border: 1px solid rgba(0, 0, 0, 0.5);
                :hover {
                    color: #fff;
                    border: 1px solid ${gradient ? gradientBorder : brandColor};
                    background: ${brandColor};
                }
            `}</style>
        </a>
    );
};

interface SchemaProps {
    faq?: FAQEntity[];
    softwareFeatureName?: FeatureName;
    howTo?: {
        howtoSteps: HowToStep[];
    };
    heroOrOgImageUrl: string;
    heroOrOgImageDescription: string;
    heroOrOgImageAlt: string;

    alternativesInfo?: {
        title: string;
        description: string;
        imageUrl: string;
    }[];
}
export interface NewLayoutProps {
    title: string;
    description: string;
    keywords?: string;
    withLiveChat?: boolean;
    ogAttributes?: {
        title?: string;
        siteName?: string;
        type?: string;
        description?: string;
        image?: string;
    };
    canonical?: string;
    showBanner?: boolean;
    noIndex?: boolean;
    isFreezone?: boolean;
    isArabic?: bool;
    schema?: SchemaProps;
    topNavBarContent?: TopNavBarContent;
}

const NewLayout: FC<PropsWithChildren<NewLayoutProps>> = (props) => {
    const { children, title, description, ogAttributes, canonical, showBanner = false, noIndex = false, isFreezone = false, isArabic = false, topNavBarContent } = props;
    const isDevEnvironment = process.env.NEXT_PUBLIC_COOKIE_DOMAIN ? process.env.NEXT_PUBLIC_COOKIE_DOMAIN.includes("gallabox.dev") : true;

    const generatedSchema = schemaGenerator(props);

    return (
        <LayoutContext.Provider value={{ showBanner }}>
            <div className={"overflow-y-auto hide-scrollbar w-screen max-h-screen overflow-x-hidden font-gb-home-body relative"} style={{ scrollBehavior: "smooth" }}>
                <Head>
                    <meta charSet="utf-8" />
                    <title>{title}</title>
                    <meta name="description" content={description} />

                    <meta name="viewport" content="width=device-width, user-scalable=no" />
                    <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                    <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                    <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                    <link rel="manifest" href="/site.webmanifest" />
                    <link rel="shortcut icon" href="/favicon.ico" />
                    <meta name="msapplication-TileColor" content="#3364FB" />
                    <meta name="theme-color" content="#3364FB" />

                    <meta name="twitter:site" content="@gallabox" />
                    <meta name="twitter:card" content="summary_large_image" />

                    <meta property="og:title" content={ogAttributes?.title ?? title} />
                    <meta property="og:site_name" content={ogAttributes?.siteName ?? "Gallabox"} />

                    <meta property="og:type" content={ogAttributes?.type ?? "website"} />
                    <meta property="og:description" content={ogAttributes?.description ?? description} />
                    <meta property="og:image" content={ogAttributes?.image ?? "https://assets.gallabox.com/gb-home/preview-image.jpg"} />
                    <meta property="og:image:url" content={ogAttributes?.image ?? "https://assets.gallabox.com/gb-home/preview-image.jpg"} />
                    <meta property="og:image:secure_url" content={ogAttributes?.image ?? "https://assets.gallabox.com/gb-home/preview-image.jpg"} />
                    <link rel="preload" href="/fonts/Google_Sans/GoogleSans-Bold.ttf" as="font" crossOrigin="" />
                    <link rel="preload" href="/fonts/Google_Sans/GoogleSans-Medium.ttf" as="font" crossOrigin="" />
                    <link rel="preload" href="/fonts/Google_Sans/GoogleSans-Regular.ttf" as="font" crossOrigin="" />
                    {canonical && <link rel="canonical" href={canonical} />}
                    {isDevEnvironment || noIndex ? <meta name="robots" content="noindex, unfollow" /> : <meta name="robots" content="index, follow" />}

                    {generatedSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generatedSchema) }} />}
                </Head>
                <div className={`bg-white ${showBanner ? "lg:pt-8 pt-0" : ""} `}>
                    <NavMenu cx={"fixed top-0 "} showBanner={showBanner} isFreezone={isFreezone} isArabic={isArabic} topNavBarContent={topNavBarContent} />
                    {children}
                    <Footer />
                    <Analytics />
                </div>

                <style jsx>{`
                    ::-webkit-scrollbar {
                        width: 8px;
                    }

                    ::-webkit-scrollbar-track {
                        border-radius: 10px;
                    }

                    ::-webkit-scrollbar-thumb {
                        border-radius: 10px;
                        background-color: #00000050;
                    }
                `}</style>
            </div>
        </LayoutContext.Provider>
    );
};

export default NewLayout;
